/**
 * Dashboard widget visibility: module + RBAC permission gates.
 * Used by CompanyDashboard for section and widget rendering.
 */

export const DASHBOARD_WIDGETS = {
  employees: {
    modules: ['HRM'],
    permissions: ['hr.employee.view_self', 'hr.employee.view_team', 'hr.employee.view_all'],
  },
  shifts: {
    modules: ['Scheduling'],
    permissions: ['scheduling.shift.view', 'scheduling.shift.view_all'],
  },
  invoices: {
    modules: ['Invoicing'],
    permissions: ['billing.invoice.view'],
  },
  funding: {
    modules: ['NDIS'],
    permissions: ['participants.funding.view', 'participants.funding.budget.view'],
  },
  compliance: {
    modules: ['HRM'],
    permissions: [
      'compliance.dashboard.view',
      'compliance.documents.view_all',
      'compliance.documents.view_team',
      'compliance.documents.view_own',
    ],
  },
  incidents: {
    modules: ['Incidents'],
    permissions: ['incident.report.view_own', 'incident.report.view_team', 'incident.report.view_all'],
  },
  jobs: {
    modules: ['Recruitment'],
    permissions: ['recruitment.jobs.view'],
  },
  reference_checks: {
    modules: ['Recruitment'],
    permissions: ['recruitment.reference_checks.view'],
  },
  leave: {
    modules: ['HRM'],
    permissions: ['leave.request.approve', 'leave.request.view_all', 'leave.request.view_team'],
  },
  policy: {
    modules: ['Policy'],
    permissions: ['policy.view', 'policy.manage'],
  },
  esignatures: {
    modules: ['E-Sign'],
    permissions: ['documents.esignature.view', 'documents.esignature.create'],
  },
  quotes: {
    modules: ['NDIS'],
    permissions: ['quotes.view'],
  },
  service_agreements: {
    modules: ['NDIS'],
    permissions: ['quotes.service_agreement.view'],
  },
  compliance_documents: {
    modules: ['HRM'],
    permissions: [
      'compliance.documents.view_all',
      'compliance.documents.view_team',
      'compliance.documents.view_own',
    ],
  },
  invoice_followups: {
    modules: ['Invoicing'],
    permissions: ['billing.invoice.view'],
  },
};

export const QUICK_ACTIONS = [
  { label: 'Create Shift', icon: 'Calendar', route: '/schedule', module: 'Scheduling', color: 'bg-purple-500', desc: 'Schedule new work', permission: 'scheduling.shift.create' },
  { label: 'Add Employee', icon: 'Users', route: '/employees/create', module: 'HRM', color: 'bg-blue-500', desc: 'Onboard new staff', permission: 'hr.employee.create' },
  { label: 'New Invoice', icon: 'DollarSign', route: '/invoices/wizard', module: 'Invoicing', color: 'bg-emerald-500', desc: 'Bill a client', permission: 'billing.invoice.create' },
  { label: 'Add Participant', icon: 'Building', route: '/participants/create', module: 'CRM', color: 'bg-orange-500', desc: 'New client intake', permission: 'participants.create' },
  { label: 'Compliance', icon: 'Shield', route: '/company/compliance', module: 'HRM', color: 'bg-rose-500', desc: 'Check status', permission: 'compliance.dashboard.view' },
  { label: 'Post Job', icon: 'Briefcase', route: '/recruitment/jobs/new', module: 'Recruitment', color: 'bg-cyan-500', desc: 'Recruitment', permission: 'recruitment.jobs.create' },
];

export function canShowDashboardWidget(widgetId, { availableModules = [], hasAnyPermission, isAdmin }) {
  const config = DASHBOARD_WIDGETS[widgetId];
  if (!config) return false;
  if (isAdmin) {
    return !config.modules?.length || config.modules.some((m) => availableModules.includes(m));
  }
  const moduleOk = !config.modules?.length || config.modules.some((m) => availableModules.includes(m));
  const permOk = !config.permissions?.length || hasAnyPermission(...config.permissions);
  return moduleOk && permOk;
}

export function getVisibleWidgets({ availableModules = [], hasAnyPermission, isAdmin }) {
  return Object.keys(DASHBOARD_WIDGETS).filter((id) =>
    canShowDashboardWidget(id, { availableModules, hasAnyPermission, isAdmin })
  );
}
